import { Modal } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckToSlot,
  faFilter,
  faPlay,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HomeAnalyticsContext,
  trackHomeCreateCampaignClicked,
  trackHomeVideoPlayClicked,
} from "analytics";
import "./LaunchCampaignPrompt.scss";

const ARCADE_ID = "JjYrQnZ1Eh1uem92vU8w";

const ARCADE_VIDEO_URL =
  `https://demo.arcade.software/${ARCADE_ID}` +
  "?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true&autoplay=true";

const VIDEO_THUMBNAIL_URL = `https://app.arcade.software/og/${ARCADE_ID}`;

interface LaunchCampaignPromptProps {
  analyticsContext: HomeAnalyticsContext;
  className?: string;
}

const LaunchCampaignPrompt: React.FC<LaunchCampaignPromptProps> = ({
  analyticsContext,
  className,
}) => {
  const navigate = useNavigate();
  const [videoModalOpen, setVideoModalOpen] = useState<boolean>(false);

  const handleStartCampaignClick = () => {
    trackHomeCreateCampaignClicked(analyticsContext);
    navigate("/campaign/new");
  };

  const handleVideoClick = () => {
    trackHomeVideoPlayClicked(analyticsContext);
    setVideoModalOpen(true);
  };

  return (
    <section className={`launch-campaign-prompt ${className || ""}`.trim()}>
      <div className="launch-campaign-prompt__hero">
        <h1>Launch Your Campaign</h1>
        <p>
          Connect with top creators, manage collaborations seamlessly, and track
          your brand&apos;s growth all in one place.
        </p>
      </div>

      <div className="launch-campaign-prompt__grid">
        <button
          className="launch-campaign-prompt__video-card"
          type="button"
          onClick={handleVideoClick}
          aria-label="Watch how Mustard works"
        >
          <div className="launch-campaign-prompt__video-frame">
            <img
              src={VIDEO_THUMBNAIL_URL}
              alt="Mustard platform overview video thumbnail"
            />
            <span className="launch-campaign-prompt__play">
              <FontAwesomeIcon icon={faPlay} />
            </span>
            <span className="launch-campaign-prompt__video-label">
              Platform Overview
            </span>
          </div>
          <div className="launch-campaign-prompt__video-copy">
            <h2>Watch: How Mustard Works</h2>
            <p>Learn the basics of launching your first successful creator campaign.</p>
          </div>
        </button>

        <div className="launch-campaign-prompt__cta-card">
          <h2>Create a Campaign</h2>
          <p>
            Reach the entire Mustard community in bulk. We&apos;ll send mobile
            and email invites on your behalf asking our members to apply based
            on your criteria.
          </p>

          <ul>
            <li>
              <FontAwesomeIcon icon={faUsers} />
              <span>Automated invites sent to matching creators</span>
            </li>
            <li>
              <FontAwesomeIcon icon={faFilter} />
              <span>Smart filters for audience demographics</span>
            </li>
            <li>
              <FontAwesomeIcon icon={faCheckToSlot} />
              <span>Review creators applications before booking</span>
            </li>
          </ul>

          <button
            className="launch-campaign-prompt__start"
            type="button"
            onClick={handleStartCampaignClick}
          >
            Start Campaign
          </button>
        </div>
      </div>

      <Modal
        className="launch-campaign-prompt__modal"
        title="Watch: How Mustard Works"
        open={videoModalOpen}
        onCancel={() => setVideoModalOpen(false)}
        footer={null}
        width={960}
        destroyOnHidden
      >
        {videoModalOpen && (
          <div className="launch-campaign-prompt__iframe-wrap">
            <iframe
              src={ARCADE_VIDEO_URL}
              title="How Mustard Works"
              allow="autoplay; fullscreen"
              allowFullScreen
              loading="lazy"
            />
          </div>
        )}
      </Modal>
    </section>
  );
};

export default LaunchCampaignPrompt;
